import React from "react";
import { AbsoluteFill, useCurrentFrame, interpolate } from "remotion";
import type { SeriesTheme } from "../styles/theme";

interface BackgroundProps {
  theme: SeriesTheme;
  variant?: "deep" | "warm" | "neutral";
}

export const Background: React.FC<BackgroundProps> = ({
  theme,
  variant = "deep",
}) => {
  const frame = useCurrentFrame();

  // Slow breathing pulse: scale oscillates 1.0 → 1.03 over 120 frames (4s at 30fps)
  const breathCycle = Math.sin((frame / 120) * Math.PI * 2);
  const scale = 1 + breathCycle * 0.015;

  // Subtle rotation for organic feel
  const rotation = Math.sin((frame / 300) * Math.PI * 2) * 0.3;

  const gradients: Record<string, string> = {
    deep: `radial-gradient(ellipse at 50% 40%, ${theme.gradientEnd} 0%, ${theme.gradientStart} 70%)`,
    warm: `radial-gradient(ellipse at 50% 50%, ${theme.accentColor}22 0%, ${theme.gradientStart} 60%)`,
    neutral: `radial-gradient(ellipse at 50% 50%, #2D2D2D 0%, #1a1a1a 70%)`,
  };

  // Floating particles (subtle dust motes)
  const particles = Array.from({ length: 12 }, (_, i) => {
    const x = ((i * 137.508) % 100); // Golden angle distribution
    const y = ((i * 89.7) % 100);
    const speed = 0.05 + (i % 3) * 0.02;
    const size = 2 + (i % 4);
    const opacity = interpolate(
      Math.sin((frame * speed + i * 50) / 30),
      [-1, 1],
      [0.02, 0.06]
    );

    return {
      x: x + Math.sin((frame * speed + i * 100) / 60) * 3,
      y: y - (frame * speed * 0.5 + i * 20) % 120 + 10,
      size,
      opacity,
    };
  });

  return (
    <AbsoluteFill>
      {/* Main gradient background */}
      <div
        style={{
          width: "100%",
          height: "100%",
          background: gradients[variant] || gradients.deep,
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          transformOrigin: "center center",
        }}
      />

      {/* Ambient light glow */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "30%",
          width: "40%",
          height: "40%",
          background: `radial-gradient(ellipse, ${theme.accentColor}08 0%, transparent 70%)`,
          opacity: interpolate(breathCycle, [-1, 1], [0.3, 0.7]),
        }}
      />

      {/* Floating particles */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            backgroundColor: theme.accentColor,
            opacity: p.opacity,
          }}
        />
      ))}

      {/* Vignette overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background:
            "radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.4) 100%)",
        }}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
